import { TimePicker } from "@tql/ui-components-react/kendo/dateinputs";
import { TextArea } from "@tql/ui-components-react/kendo/inputs";
import { Error, Label } from "@tql/ui-components-react/kendo/labels";
import { DatePicker } from "@tql/ui-components-react/tql/dateinputs";
import type { Dispatch, SetStateAction } from "react";

import type { FlagFormErrors, FlagFormValues } from "./utils/validation.ts";

type FlagFormProps = {
	readonly values: FlagFormValues;
	readonly setValues: Dispatch<SetStateAction<FlagFormValues>>;
	readonly errors: FlagFormErrors;
	readonly showFollowupError: boolean;
	readonly showDescriptionError: boolean;
	readonly followupDateId: string;
	readonly followupDateErrorId: string;
	readonly followupTimeId: string;
	readonly followupTimeErrorId: string;
	readonly reasonId: string;
	readonly errorId: string;
};

export const FlagForm = ({
	values,
	setValues,
	errors,
	showFollowupError,
	showDescriptionError,
	followupDateId,
	followupDateErrorId,
	followupTimeId,
	followupTimeErrorId,
	reasonId,
	errorId,
}: FlagFormProps) => {
	const showDateError = showFollowupError && Boolean(errors.followupDate);
	const showTimeError = showFollowupError && Boolean(errors.followupTime);

	return (
		<div className={"flag-form"}>
			<div className={"flag-row"}>
				<div className={"flag-field-col flag-field-col-date"}>
					<Label editorId={followupDateId}>
						Shipment follow up date
						<span className={"required"}>&nbsp;*</span>
					</Label>
					<DatePicker
						ariaDescribedBy={showDateError ? followupDateErrorId : undefined}
						id={followupDateId}
						onChange={(event) =>
							setValues((prev) => ({
								...prev,
								followupDate: event.target.value,
							}))
						}
						valid={!showDateError}
						value={values.followupDate}
					/>
					<div className={"flag-error-slot"}>
						{showDateError ? (
							<Error id={followupDateErrorId}>{errors.followupDate}</Error>
						) : null}
					</div>
				</div>
				<div className={"flag-field-col flag-field-col-time"}>
					{/* TimePicker puts `id` on both its wrapper and input, so a
					    `for` label would target the wrapper; label via aria instead. */}
					<Label id={`${followupTimeId}_label`}>
						Time<span className={"required"}>&nbsp;*</span>
					</Label>
					<TimePicker
						ariaDescribedBy={showTimeError ? followupTimeErrorId : undefined}
						ariaLabelledBy={`${followupTimeId}_label`}
						id={followupTimeId}
						onChange={(event) =>
							setValues((prev) => ({
								...prev,
								followupTime: event.target.value,
							}))
						}
						valid={!showTimeError}
						value={values.followupTime}
					/>
					<div className={"flag-error-slot"}>
						{showTimeError ? (
							<Error id={followupTimeErrorId}>{errors.followupTime}</Error>
						) : null}
					</div>
				</div>
			</div>
			<label htmlFor={reasonId}>
				Follow up reason
				<span className={"required"}>&nbsp;*</span>
			</label>
			<TextArea
				aria-invalid={showDescriptionError}
				ariaDescribedBy={showDescriptionError ? errorId : undefined}
				className={"flag-reason-input"}
				id={reasonId}
				onChange={(event) =>
					setValues((prev) => ({
						...prev,
						description: event.value,
					}))
				}
				placeholder={"Enter the reason this shipment needs followed up on..."}
				valid={!showDescriptionError}
				value={values.description}
			/>
			{showDescriptionError ? (
				<Error id={errorId}>{errors.description}</Error>
			) : null}
		</div>
	);
};
